/**
 * services/chats-service.js — load + transform chat data into the store.
 *
 * Bridges the CRM API response shape into the normalized format our UI uses.
 * Components never call the API directly — they call these actions which
 * update the store, and the components re-render reactively.
 */

import * as api from './api.js';
import { setChatList, addChat, updateChat, $chats, $serverAccountUnread, makeChatKey } from '../stores/chats.js';
import { normalizePhone } from './phone.js';
import { isPermissionDeniedError, markDegraded, markHealthy } from './permission-degraded.js';

/**
 * Normalize an exploded /chats row into our store shape.
 *
 * #153: the proxy now fans out one CRM contact into N rows — one per
 * channel the contact has messaged on — and stamps each row with a
 * `chat_key` (contactId:channelId) plus per-channel fields
 * (channel_phone_id, last_message_preview, channel_unread_count). This
 * function maps those to the store shape. The CRM contactId is preserved
 * on the row so all contact-scoped CRM API calls (assign, done, tags, CF)
 * still operate on a single contact record even though the chat list
 * shows multiple rows for it.
 *
 * Returns null for rows with an unusable identifier. loadChats filters
 * those out before handing the list to setChatList. Without this guard
 * (#117), multiple chats whose identifier normalizes to an empty string
 * collapse onto the same empty-string key in $phoneIndex, and realtime
 * inserts for those chats resolve to the wrong row.
 */
function normalizeChat(raw) {
  const firstName = raw.name?.first_name || '';
  const lastName  = raw.name?.last_name || '';
  const fullName  = (firstName + ' ' + lastName).trim();
  const rawPhone  = raw.profile?.whatsapp?.identifier
                  || raw.profile?.identifier
                  || '';
  const phone     = normalizePhone(rawPhone); // always full E.164-ish
  if (!phone) {
    console.warn('[v2 chats] dropping chat with empty phone:', raw._id || raw.id || '(unknown id)');
    return null;
  }
  const profilePic = raw.profile?.whatsapp?.profile_pic || '';

  // Extract assigned agent
  const agent = raw.agent;
  let agentEmail = null;
  if (agent && typeof agent === 'object') {
    agentEmail = agent.username || agent.email || null;
  } else if (typeof agent === 'string' && agent) {
    agentEmail = agent;
  }

  const contactId     = raw._id || raw.id;
  const channelPhoneId = raw.channel_phone_id || null;
  // Fall back to composing the key client-side if the proxy didn't stamp
  // one (e.g. during a mid-rollout when an old proxy answers a new client).
  const chatKey        = raw.chat_key || makeChatKey(contactId, channelPhoneId);

  return {
    // #153: `id` is the composite chat_key used as the store map key and the
    // React render key. `contactId` is the CRM ObjectId used by all CRM API
    // calls (assign, done, tags, CF) — contact-scoped actions must unwrap it.
    id:         chatKey,
    contactId,
    name:       fullName || phone || 'Unknown',
    phone,
    profilePic,
    agent:      agentEmail,
    botActive:  raw.bot?.pause === false,
    lastSeen:   raw.last_seen || 0,
    // Per-channel activity timestamps from the annotation RPC. Drive the
    // chat-row session-window badge + the 24h send-window guard in Thread.
    lastMessageTs: raw.last_message_ts || 0,
    lastInboundTs: raw.last_inbound_ts || 0,
    // Per-channel unread count. CRM's `unread_count` is per contact (would
    // double-count across channels); the proxy's `channel_unread_count` is
    // per (contact, channel). Fall back to the CRM field for orphan rows
    // with no inbox_messages history (channel_unread_count absent).
    unread:     (raw.channel_unread_count != null ? raw.channel_unread_count : (raw.unread_count || 0)),
    tags:       raw.tags || [],
    lastMessage: raw.last_message_preview
              || raw.last_message?.text?.body
              || raw.last_message_text
              || '',
    createdAt:  raw.createdAt,
    // Channel-scoped color + prefix. Rows without a channelPhoneId are
    // orphans (no inbox_messages history) and only appear under "All".
    channelPhoneId,
    // #163: per-row done flag from the shadow table. Populated by the
    // /chats fan-out when a shadow row for this (contact, channel)
    // has is_done=true. When the shadow has no entry or is_done=false,
    // this is false.
    isDone:     !!raw.is_done,
    // Lead-source / campaign provenance from the CRM contact's custom
    // fields. Used by Thread.js to render an inline "template sent"
    // card after the trigger keyword bubble — gives the agent context
    // when the outbound marketing template never landed in
    // inbox_messages (campaign sends from Partner Panel don't echo via
    // event_type='outgoing' to our webhook).
    customFieldHash: raw.custom_field_hash || null,
    // Debug only — keep the raw envelope so the browser console can diff
    // store state against what the server sent.
    _raw: raw,
  };
}

/** Load chat list from API into the store */
export async function loadChats({ filter = 'all', agent = null } = {}) {
  const opts = {};
  if (filter === 'mine' && agent) opts.agent = agent;
  if (filter === 'unread')        opts.status = 'unread';
  if (filter === 'unassigned')    opts.status = 'unassigned';

  let data;
  try {
    data = await api.fetchChats(opts);
    markHealthy('chats');
  } catch (e) {
    // #168 — surface 403 as a degraded-permissions hint. /chats is the
    // primary CRM-forwarded endpoint; if it 403s the chat list will
    // appear empty with no explanation. Tag it so the boot banner
    // points at the dashboard checklist.
    if (isPermissionDeniedError(e)) {
      markDegraded('chats', e.message);
    }
    throw e;
  }
  const rawChats = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  // #117: normalizeChat returns null for rows with an empty phone.
  // Filter them out here so the store never sees unusable entries.
  const chats = rawChats.map(normalizeChat).filter(Boolean);
  const dropped = rawChats.length - chats.length;
  if (dropped > 0) {
    console.warn(`[v2 chats] dropped ${dropped} chats with unusable identifiers`);
  }
  setChatList(chats);

  // T14 — server-stamped count feeds $serverAccountUnread (the floor
  // for paginated-out chats). The derived $accountUnread computes
  // max(local sum, server count) so the OS badge stays correct even
  // when the server has more unread chats than our /chats page showed.
  const serverUnread = Number(data?.meta?.account_unread);
  $serverAccountUnread.set(Number.isFinite(serverUnread) ? serverUnread : 0);

  return chats;
}

/**
 * Search chats by custom field value.
 *
 * The CRM's /crm/chat/search endpoint is scoped STRICTLY to custom field
 * lookups — `search_field` expects a custom field ID, not 'name' or
 * 'profile.whatsapp.identifier'. Verified 2026-04-14 by probing all the
 * plausible field names with zero results. For name/phone search we use
 * the client-side filter on $filteredChats in ChatList.
 *
 * This helper is kept for future workflows that need to locate a contact
 * by a specific CF value (e.g., "find the contact whose city = Mumbai").
 * Caller must supply the actual custom field slug.
 */
export async function searchChatsByCustomField(fieldId, value) {
  const data = await api.searchChats(String(value || '').trim(), fieldId);
  const rawChats = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
  const chats = rawChats.map(normalizeChat);
  setChatList(chats);
  return chats;
}

/** Get a single chat from the store (non-reactive) */
export function getChat(chatId) {
  return $chats.get()[chatId] || null;
}

/** Update a chat's assigned agent (after assign API call) */
export function updateChatAgent(chatId, agentEmail) {
  updateChat(chatId, { agent: agentEmail });
}
